import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EkasiServeAppBar, equalArray, LoadingPage, selectAllOnFocus, SetState } from "./Common";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DeleteIcon from '@mui/icons-material/Delete';
import { Container, FormControl, IconButton, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Client } from "./Clients";
import { Add, Edit } from "@mui/icons-material";
import { Company } from "./Companies";

export interface User {
    username: string;
	role: number;
    clients: Array<number>;

    password: string;
    password_2: string;
    isCreation: boolean;
}

export interface UserPalette {
    main: string;
    secondary: string;
    mode: "light" | "dark" | "system";
}

function formatUserRole(role: number): string {
    if (role === 1) {
        return "Admin";
    } else if (role === 2) {
        return "Employee"
    } else if (role === 4) {
        return "Client";
    } else {
        return "Unknown";
    }
}

function formatToRole(role: string): number {
    if (role === "Admin") {
        return 1;
    } else if (role === "Employee") {
        return 2;
    } else if (role === "Client") {
        return 4;
    } else {
        return 0;
    }
}

interface IUserContext {
    user: Partial<User>;
    setUser: SetState<Partial<User>>;
    palette: UserPalette;
    setPalette: SetState<UserPalette>;
}

export const UserContext = React.createContext<IUserContext>({user: {}, setUser: () => {}, palette: { main: "blue", secondary: "blue", mode: "system" }, setPalette: () => {}});
export const UserRoleAdmin = 1;
export const UserRoleEmployee = 2;
export const UserRoleClient = 4;
export const UserRoleUnknown = 1 << 30;
export function userIsEmployeeOrAdmin(auth: IUserContext): boolean {
    return auth.user.role === UserRoleAdmin || auth.user.role === UserRoleEmployee;
}

export function UsersTable({ users }: { users: Array<User> }) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="50%">Username</TableCell>
                                    <TableCell width="45%">Role</TableCell>
                                    <TableCell width="5%">
                                        <Button variant="contained" component={Link} to={"/users/0"} fullWidth>
                                            <Add />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((row) => (
                                    <TableRow key={row.username}>
                                        <TableCell>{row.username}</TableCell>
                                        <TableCell>{formatUserRole(row.role)}</TableCell>
                                        <TableCell>
                                            <Button variant="contained" component={Link} to={"/users/" + encodeURIComponent(row.username)} fullWidth>
                                                <Edit />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>
        </Grid>
    )
}

export const UsersPage = () => {
    const [users, setUsers] = useState<Array<User>>([]);
    const [snackbar, setSnackbar] = useState("");

    useEffect(() => {
        const fetchUsers = async () => {
            const response = await fetch("/api/v1/users");
            const data = await response.json();
            setUsers(data.result || []);
            if (data.error) {
                setSnackbar(data.error);
            }
        };
        fetchUsers();
    }, []);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={12}> <h1> Users </h1> </Grid>
                <Grid size={12}>
                    <UsersTable users={users}/>
                </Grid>
            </Grid>
            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
        </Stack>
    )
}

export function hexFromColorName(color: string): string {
    switch (color) {
    case "red": return "#f44336";
    case "pink": return "#e91e63";
    case "purple": return "#9c27b0";
    case "deepPurple": return "#673ab7";
    case "indigo": return "#3f51b5";
    case "blue": return "#2196f3";
    case "lightBlue": return "#03a9f4";
    case "cyan": return "#00bcd4";
    case "teal": return "#009688";
    case "green": return "#4caf50";
    case "lightGreen": return "#8bc34a";
    case "lime": return "#cddc39";
    case "yellow": return "#ffeb3b";
    case "amber": return "#ffc107";
    case "orange": return "#ff9800";
    case "deepOrange": return "#ff5722";
    }
    return "#2196f3";
}

export function UserForm({ user, setUser, clients, companies, setSnackbar }: { user: Partial<User>, setUser: SetState<Partial<User>>, clients: Array<Client>, companies: Array<Company>, setSnackbar: SetState<string> }) {
    const changePassword = async () => {
        if (user.password !== "" && user.password !== user.password_2) {
            setSnackbar("Passwords do not match");
            return;
        }
        const response = await fetch("/api/v1/users/password", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ "username": user.username, "password": user.password })
        });
        const data = await response.json();
        if (data.error) {
            setSnackbar(data.error);
        } else {
            setSnackbar("Information Saved");
        }
    }
    const { user: authUser, palette, setPalette } = useContext(UserContext);
    const [selectedCompanies, setSelectedCompanies] = useState<Array<number>>([]);
    useEffect(() => {
        localStorage.setItem("theme.palette.main", palette.main);
    }, [palette, setPalette]);
    useEffect(() => {
        localStorage.setItem("theme.palette.secondary", palette.secondary);
    }, [palette, setPalette]);
    useEffect(() => {
        localStorage.setItem("theme.mode", palette.mode);
    }, [palette, setPalette]);
    const colors = ["red", "pink", "purple", "deepPurple", "indigo", "blue", "lightBlue", "cyan", "teal", "green", "lightGreen", "lime", "yellow", "amber", "orange", "deepOrange"];

    useEffect(() => {
        setSelectedCompanies(user.clients?.map(client => companies.find(company => company.id === clients.find(c => c.id === client)?.company_id)?.id || 0) || []);
    }, [companies, clients, user]);

    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            {authUser.role === 1 && <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="username"
                    label="Username"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setUser(user => ({ ...user, username: e.target.value }))}
                    fullWidth
                    value={user.username || ""} />
            </Grid>}
            {authUser.role === 1 && <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select
                        label="Role"
                        value={user.role ? formatUserRole(user.role) : ""}
                        onChange={(ev) => setUser(user => ({ ...user, role: formatToRole(ev.target.value) }))}>
                        <MenuItem value={formatUserRole(4)}> {formatUserRole(4)} </MenuItem>
                        <MenuItem value={formatUserRole(2)}> {formatUserRole(2)} </MenuItem>
                        <MenuItem value={formatUserRole(1)}> {formatUserRole(1)} </MenuItem>
                    </Select>
                </FormControl>
            </Grid>}
            <Grid size={{ xs: 12, md: user.isCreation ? 6 : 4 }}>
                <TextField
                    id="password_new"
                    label={user.isCreation ? "Password" : "New Password"}
                    variant="outlined"
                    type="password"
                    autoComplete="off"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setUser(user => ({ ...user, password: e.target.value }))}
                    fullWidth
                    value={user.password || ""} />
            </Grid>
            <Grid size={{ xs: 12, md: user.isCreation ? 6 : 4 }}>
                <TextField
                    id="password_2"
                    label="Confirm Password"
                    variant="outlined"
                    type="password"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setUser(user => ({ ...user, password_2: e.target.value }))}
                    fullWidth
                    value={user.password_2 || ""} />
            </Grid>
            { !user.isCreation && <Fragment>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Button variant="contained" onClick={changePassword} fullWidth>
                            Change Password
                        </Button>
                    </Grid>
                </Fragment>
            }
            {
                !user.isCreation && <Fragment>
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>Mode</InputLabel>
                            <Select
                                label="Mode"
                                value={palette.mode || "system"}
                                onChange={(ev) => setPalette(p => ({ ...p, mode: ev.target.value }))}
                            >
                                <MenuItem value="system">Auto</MenuItem>
                                <MenuItem value="light">Light</MenuItem>
                                <MenuItem value="dark">Dark</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <Stack direction={"column"}>
                            <ToggleButtonGroup color="primary" value={palette.main} exclusive fullWidth>
                                {
                                    colors.map((color) => (<ToggleButton color="primary" value={color} sx={{ backgroundColor: hexFromColorName(color) }} onClick={() => setPalette(p => ({ ...p, main: color}))}></ToggleButton>))
                                }
                            </ToggleButtonGroup>
                            <ToggleButtonGroup color="secondary" value={palette.secondary} exclusive fullWidth>
                                {
                                    colors.map((color) => (<ToggleButton color="secondary" value={color} sx={{ backgroundColor: hexFromColorName(color) }} onClick={() => setPalette(p => ({ ...p, secondary: color }))}></ToggleButton>))
                                }
                            </ToggleButtonGroup>
                        </Stack>
                    </Grid>
                </Fragment>
            }
            {
                user.role !== UserRoleAdmin && <Grid size={12}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width="20%">Associated Company</TableCell>
                                <TableCell width="60%">Associated Client</TableCell>
                                <TableCell width="20%">
                                    { 
                                        authUser.role === UserRoleAdmin
                                        ? <Button variant="contained" onClick={() => setUser(usr => ({ ...usr, clients: usr.clients ? [...usr.clients, 0] : [0] }))}  fullWidth>
                                            +
                                        </Button>
                                        : <></>
                                    }
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                user.clients?.map((client, idx) =>
                                    authUser.role === UserRoleAdmin
                                    ? <TableRow key={client}>
                                        <TableCell>
                                            <FormControl fullWidth>
                                                <InputLabel>Company</InputLabel>
                                                <Select
                                                    label="Company"
                                                    value={companies.length === 0 ? "" : (selectedCompanies[idx] || "")}
                                                    onChange={(ev) => setSelectedCompanies(companies => companies.map((company, jdx) => jdx === idx ? ev.target.value : company))}>
                                                    {
                                                        (companies.map((company) => (<MenuItem key={company.id} value={company.id}> {company.name} </MenuItem>)))
                                                    }
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl fullWidth>
                                                <InputLabel>Client</InputLabel>
                                                <Select
                                                    label="Clients"
                                                    value={clients.length === 0 || selectedCompanies[idx] === 0 ? "" : client}
                                                    onChange={(ev) => setUser(user => ({ ...user, clients: (user.clients || []).map((client, jdx) => idx === jdx ? ev.target.value : client ) || [] }))}>
                                                    {
                                                        (clients.filter((client) => client.company_id === selectedCompanies[idx]).map((client) => (<MenuItem key={client.id} value={client.id}> {client.name} ({client.quote_id}) </MenuItem>)))
                                                    }
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell align="center" onClick={ev => setUser(usr => ({ ...usr, clients: usr.clients?.filter((c, jdx) => idx !== jdx) || [] }) )}>
                                            <IconButton color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    : <TableRow key={client}>
                                        <TableCell>
                                            {companies.find(company => company.id === clients.find(c => c.id === client)?.company_id)?.name || ""}
                                        </TableCell>
                                        <TableCell>
                                            {(
                                                () => {
                                                    var data = clients.find((c) => c.id === client);
                                                    return (data?.name || "") + " (" + (data?.quote_id || "") + ")"
                                                }
                                            )()}
                                        </TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                ) || <></>
                            }
                        </TableBody>
                    </Table>
                </Grid>
            }
        </Grid>
    )
}

export const UserPage = () => {
    const { username } = useParams();
    const [user, setUser] = useState<Partial<User>>({});
    const [companies, setCompanies] = useState<Array<Company>>([]);
    const [clients, setClients] = useState<Array<Client>>([]);
    const [snackbar, setSnackbar] = useState("");
    const [requestDelete, setRequestDelete] = useState(false);
    const [edited, setEdited] = useState(false);
    const [savedUser, setSavedUser] = useState<Partial<User>>({});
    const [isLoading, setIsLoading] = useState(0);
    const navigate = useNavigate();
    const auth = useContext(UserContext);

    useEffect(() => {
        const fetchData = async () => {
            const userResponse = await fetch("/api/v1/users/" + encodeURIComponent(username || "0"));
            const userData = await userResponse.json();
            if (userData.result) {
                userData.result.isCreation = !userData.result.username;
            }
            setUser(userData.result || {});
            setSavedUser(userData.result || {});
            if (userData.error) {
                setSnackbar(userData.error);
            }

            const companiesResponse = await fetch("/api/v1/companies");
            const companiesData = await companiesResponse.json();
            setCompanies(companiesData.result || []);
            if (companiesData.error) {
                setSnackbar(companiesData.error);
            }

            const clientsResponse = await fetch("/api/v1/clients?company_id=0");
            const clientsData = await clientsResponse.json();
            setClients(clientsData.result || []);
            if (clientsData.error) {
                setSnackbar(clientsData.error);
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [username]);

    useEffect(() => {
        setEdited(!(user.username === savedUser.username && user.role === savedUser.role && equalArray(user.clients || [], savedUser.clients || [])));
    }, [user.username, savedUser.username, user.role, savedUser.role, user.clients, savedUser.clients]);

    const saveUser = useCallback(async () => {
        if (user.isCreation && user.password !== user.password_2) {
            setSnackbar("Passwords do not match");
            return;
        }
        if (user.role === 0 || (user.role || UserRoleUnknown) > UserRoleClient) {
            setSnackbar("User does not have a role");
            return;
        }

        const response = await fetch("/api/v1/users", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({"user": user, "clients": user.clients || []})
        });
        const data = await response.json();
        if (data.result) {
            setUser(data.result);
            setSavedUser(data.result);
            setSnackbar("Information Saved");
        } else if (data.error) {
            setSnackbar(data.error);
        }
    }, [user]);

    const deleteUser = useCallback(async () => {
        const response = await fetch("/api/v1/users", { 
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({"username": user.username, "password": user.password})
        });
        const data = await response.json();
        if (data.result) {
            navigate(-1);
        } else if (data.error) {
            setSnackbar(data.error);
        }
    }, [navigate, user.password, user.username]);

    if (isLoading) return (<LoadingPage />);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={{ xs: 12, md: auth.user.role === 1 ? 8 : 12 }}> <h1> {user.username || "New User"} </h1> </Grid>
                {auth.user.role === 1 && <Grid size={{ xs: 6, md: 2 }}>
                    {(user.isCreation)
                        ? <Button variant="contained" onClick={() => navigate(-1)} fullWidth> Cancel </Button>
                        : <Button variant="contained" onClick={() => setRequestDelete(true)} color="error" fullWidth> Delete </Button>
                    }
                </Grid>}
                {auth.user.role === 1 && <Grid size={{ xs: 6, md: 2 }}>
                    <Button disabled={!edited} variant="contained" onClick={saveUser} fullWidth> Save </Button>
                </Grid>}
                <Grid size={12}>
                    <UserForm user={user} setUser={setUser} clients={clients} companies={companies} setSnackbar={setSnackbar} />
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
            <Dialog open={requestDelete}>
                <DialogTitle> Are you sure you want to delete this product? </DialogTitle>
                <Button onClick={deleteUser}> Yes, Delete </Button>
                <Button onClick={() => setRequestDelete(false)}> No, Cancel </Button>
            </Dialog>
        </Stack>

    )
}